import { useFetch } from "@/hooks/useFetch";
import { useState } from "react";
import { StatCard } from "@/components/StatCard";
import { DashboardBenefits } from "@/components/DashboardBenefits";
import { PendingDetailDialog } from "@/components/PendingDetails";
import { useBase } from "@/context/BaseContext";
import { computePunchPending } from "@/lib/punchPending";
import { useHolidayChecker } from "@/lib/useHolidays";
import { Loading, ErrorState } from "@/components/DataState";
import { brl, num, km } from "@/lib/format";
import {
  Users,
  PackageCheck,
  Gauge,
  Boxes,
  AlertTriangle,
  ClipboardCheck,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

export default function Dashboard() {
  const drivers = useFetch("/admin/drivers");
  const stock = useFetch("/stock");
  const pending = useFetch("/admin/pending-summary");
  const faltas = useFetch("/admin/faltas");
  const { selectedBaseId } = useBase();
  const routes = useFetch("/admin/routes");
  const pendFunc = useFetch("/admin/pending-funcionarios");
  const lgpd = useFetch("/admin/lgpd-requests", { status: "pending" });
  const punchAudit = useFetch("/admin/punch-audit");
  const collabs = useFetch("/admin/collaborators");
  const [openKey, setOpenKey] = useState(null);
  const isHoliday = useHolidayChecker();

  if (drivers.loading || stock.loading || pending.loading || faltas.loading || (selectedBaseId && (routes.loading || pendFunc.loading || lgpd.loading || collabs.loading))) return <Loading />;
  if (drivers.error) return <ErrorState error={drivers.error} onRetry={drivers.refetch} />;

  const list = drivers.data?.drivers || [];
  const totalDeliveries = list.reduce((a, d) => a + (d.deliveries_fortnight || 0), 0);
  const totalKm = list.reduce((a, d) => a + (d.km_fortnight || 0), 0);
  const s = stock.data || {};
  const alerts = (s.bobinas_low ? 1 : 0) + (s.chips_low ? 1 : 0);

  // O contador de faltas do upstream inflava o número (contava faltas "justified"
  // além das "pending"). Recalculamos a partir das faltas realmente pendentes,
  // coerente com o modal de detalhes.
  const pendingFaltas = (faltas.data?.faltas || []).filter((f) => f.status === "pending").length;
  // O /admin/pending-summary ignora base_id: com uma base selecionada, recalculamos cada contagem
  // a partir das listas da base (drivers/collaborators/faltas já vêm filtrados pelo interceptor).
  const baseCounts = (() => {
    if (!selectedBaseId) return null;
    const driverIds = new Set(list.map((d) => d.id));
    const memberIds = new Set([...driverIds, ...(collabs.data?.collaborators || []).map((c) => c.id)]);
    return {
      audits: (routes.data?.routes || []).filter((r) => (r.audit_status || "pending") === "pending" && driverIds.has(r.driver_id)).length,
      approvals: (pendFunc.data?.pending || []).filter((p) => (p.base_ids || []).includes(selectedBaseId)).length,
      faltas: pendingFaltas,
      deliveries: list.filter((d) => !d.finalized_today).length,
      lgpd: (lgpd.data?.requests || []).filter((r) => (r.status || "pending") === "pending" && memberIds.has(r.user_id)).length,
    };
  })();
  const pendingItems = (pending.data?.items || []).map((it) => (
    baseCounts && it.key in baseCounts ? { ...it, count: baseCounts[it.key] }
      : it.key === "faltas" ? { ...it, count: pendingFaltas } : it));
  // pendencias_antigas (dias anteriores) já vem filtrado pela base no upstream. Ponto é unificado e
  // calculado no painel (sem entregadores): colaboradores sem ponto + batidas pendentes.
  const OLD_META = {
    rotas_sem_km_validada: { description: "Rotas de dias anteriores aguardando validação" },
    faltas_sem_resolucao: { description: "Faltas de dias anteriores sem decisão" },
  };
  const oldData = pending.data?.pendencias_antigas;
  const windowStart = oldData?.janela_entregadores_sem_ponto?.inicio || "";
  const punchOld = computePunchPending(punchAudit.data, windowStart, isHoliday);
  const upstreamOld = (oldData?.items || []).filter((it) => OLD_META[it.key]).map((it) => ({ ...it, ...OLD_META[it.key] }));
  const pontoItem = { key: "pontos", label: "Pendências de ponto", count: punchOld.count,
    description: `${punchOld.missing.length} sem ponto · ${punchOld.pending.length} batida(s) pendente(s)` };
  const oldItems = upstreamOld.length ? [upstreamOld[0], pontoItem, ...upstreamOld.slice(1)] : (oldData ? [pontoItem] : []);
  const oldTotal = oldItems.reduce((a, it) => a + (it.count || 0), 0);
  const pendingTotal = pendingItems.reduce((a, it) => a + (it.count || 0), 0) + oldTotal;

  const chartData = [...list]
    .sort((a, b) => (b.deliveries_fortnight || 0) - (a.deliveries_fortnight || 0))
    .slice(0, 8)
    .map((d) => ({ name: (d.name || "").split(" ")[0], entregas: d.deliveries_fortnight || 0 }));

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={Users} label="Colaboradores" value={num(list.length)} accent="sky" testId="stat-collaborators" />
        <StatCard icon={PackageCheck} label="Entregas (quinzena)" value={num(totalDeliveries)} accent="orange" testId="stat-deliveries" />
        <StatCard icon={Gauge} label="KM (quinzena)" value={km(totalKm)} accent="violet" testId="stat-km" />
      </div>
      <DashboardBenefits />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Chart */}
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 lg:col-span-2">
          <h3 className="mb-4 text-base font-medium text-slate-200">
            Top entregadores por entregas (quinzena)
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="99%" height="100%" minWidth={0}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
                <XAxis dataKey="name" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip
                  cursor={{ fill: "#1e293b55" }}
                  contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#e2e8f0" }}
                />
                <Bar dataKey="entregas" fill="#E30613" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pending + stock */}
        <div className="space-y-6">
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <div className="mb-4 flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-orange-400" />
              <h3 className="text-base font-medium text-slate-200">Pendências</h3>
              <span className="ml-auto rounded bg-orange-500/15 px-2 py-0.5 text-xs font-bold text-orange-300">
                {pendingTotal}
              </span>
            </div>
            <div className="space-y-1">
              {pendingItems.map((it) => (
                <button
                  key={it.key}
                  onClick={() => setOpenKey(it.key)}
                  data-testid={`pending-item-${it.key}`}
                  className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-slate-800/60"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-slate-200">{it.label}</p>
                    <p className="truncate text-xs text-slate-500">{it.description}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${it.count > 0 ? "bg-rose-500/15 text-rose-300" : "bg-slate-800 text-slate-500"}`}>
                    {it.count}
                  </span>
                </button>
              ))}
            </div>
            {oldItems.length > 0 && (
              <div className="mt-4 border-t border-slate-800 pt-3" data-testid="pending-old-section">
                <div className="mb-2 flex items-center gap-2 px-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pendências antigas</p>
                  <span className="ml-auto rounded bg-slate-800 px-2 py-0.5 text-[10px] font-bold text-slate-400" data-testid="pending-old-total">{oldTotal}</span>
                </div>
                <div className="space-y-1">
                  {oldItems.map((it) => (
                    <button
                      key={it.key}
                      onClick={() => setOpenKey(it.key)}
                      data-testid={`pending-old-${it.key}`}
                      className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-slate-800/60"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm text-slate-200">{it.label}</p>
                        <p className="truncate text-xs text-slate-500">{it.description}</p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${it.count > 0 ? "bg-rose-500/15 text-rose-300" : "bg-slate-800 text-slate-500"}`}>
                        {it.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <div className="mb-4 flex items-center gap-2">
              <Boxes className="h-4 w-4 text-sky-400" />
              <h3 className="text-base font-medium text-slate-200">Estoque</h3>
              {alerts > 0 && (
                <span className="ml-auto flex items-center gap-1 rounded bg-rose-500/15 px-2 py-0.5 text-xs font-bold text-rose-300">
                  <AlertTriangle className="h-3 w-3" /> {alerts} alerta(s)
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500">Bobinas</p>
                <p className={`font-mono text-2xl font-bold ${s.bobinas_low ? "text-rose-400" : "text-slate-100"}`}>{num(s.bobinas_qty)}</p>
                <p className="text-[11px] text-slate-600">mín. {num(s.bobinas_min)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Chips</p>
                <p className={`font-mono text-2xl font-bold ${s.chips_low ? "text-rose-400" : "text-slate-100"}`}>{num(s.chips_qty)}</p>
                <p className="text-[11px] text-slate-600">mín. {num(s.chips_min)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {openKey && (
        <PendingDetailDialog
          item={pendingItems.find((i) => i.key === openKey) || oldItems.find((i) => i.key === openKey)}
          drivers={list}
          windowStart={windowStart}
          onClose={() => setOpenKey(null)}
          onChanged={() => { pending.refetch(); drivers.refetch(); faltas.refetch(); routes.refetch(); pendFunc.refetch(); lgpd.refetch(); punchAudit.refetch(); }}
        />
      )}
    </div>
  );
}
